import { TranslationApiResponse, TranslationElement, TranslationLanguage, TranslationPhrases, TranslationSet } from '../../types';
import { addTranslation, getAllTranslations } from '../api/organization-api';

import { HEADERS } from '../constants';
import { State } from './state-management/state';
import { getFromLocalStorage, isDefined, isValidString, setLocalStorage } from '../utils';
import organizationInfo from './organization-info';
import pointCache from './point-cache';
import { property } from 'lit/decorators';
import topicData from './topic-data';

class TranslationData extends State {

  static amplitude_callsite: string = 'Translation Data';

  @property({ type: Boolean }) isReady: boolean = false;

  @property({ type: Boolean }) isActivated: boolean = false;

  @property({ type: String }) language: string = 'en';

  @property({ type: Boolean }) liveEditMode: boolean = false;

  private coreTranslations: TranslationElement[] = [];

  private newTranslations: TranslationElement[] = [];

  allTranslationSets: TranslationSet[] = [];

  private translationSet: TranslationElement[] = [];

  private toBeTranslated: string[] = [];

  private baseMapTerms: string[] = [];

  allTranslations: TranslationElement[] = [];

  // create a stored item with the language and the translations in case the user changes the language
  // and then changes it back

  private storedTranslations: { [key: string]: TranslationElement[] } = {};

  private isTranslating: boolean = false;

  // Only viewer surfaces opt in; admin/editor pages keep starting in English.
  private autoSelectLanguage: boolean = false;

  private readonly Weglot = (window as any).Weglot;

  private readonly eventTarget = new EventTarget();

  onLanguageChanged(listener: EventListener) {
    this.eventTarget.addEventListener('languageChanged', listener);
  }

  onOriginalTextChanged(listener: EventListener) {
    this.eventTarget.addEventListener('originalTextChanged', listener);
  }

  async translateFields(fields: TranslationPhrases): Promise<TranslationPhrases> {
    // Translate concurrently so uncached phrases coalesce into one pending batch
    // instead of paying the one-second batching delay once per phrase.
    const keys = Object.keys(fields);
    const values = await Promise.all(keys.map(async (key) => await this.translatePhrase(fields[key])));
    const translatedFields: TranslationPhrases = {};
    keys.forEach((key, index) => {
      translatedFields[key] = values[index];
    });
    return translatedFields;
  }


  /**
   * Translates a given phrase.
   *
   * @param phrase - The phrase to translate.
   * @returns A promise that resolves with the translated phrase.
   */
  async translatePhrase(phrase: string): Promise<string> {
      // if the string is empty, return it
      if (phrase.trim() === '') {
        return phrase;
      }
      return await new Promise((resolve) => {
          // Check if the translation service is activated and ready.
          if (this.isActivated && this.isReady) {
              // Try to find a translation for the phrase in the existing translations.
              const translation = this.translationSet.find((t) => t.original_text === phrase)?.translated_text
                  ?? this.allTranslations.find((t) => t.original_text === phrase)?.translated_text;
              // If a translation is found, resolve the promise with the translation.
              if (translation !== undefined) {
                  resolve(translation);
              } else {
                  // If no translation is found, add the phrase to the list of phrases to be translated.
                  this.toBeTranslated.push(phrase);
                  // If the service is not currently translating anything, start translating the pending phrases.
                  if (!this.isTranslating) {
                      this.isTranslating = true;
                      this.translatePendingPhrases().then(() => {
                          // After the pending phrases are translated, find the translation for the original phrase.
                          this.isTranslating = false;
                          const newTranslation = this.allTranslations.find((t) => t.original_text === phrase)?.translated_text ?? phrase;
                          // Resolve the promise with the new translation, or with the original phrase if no translation was found.
                          resolve(newTranslation);
                      }).catch(error => {
                          // If an error occurs during translation, log the error and resolve the promise with the original phrase.
                          console.error('An error occurred while translating the phrase:', error);
                          resolve(phrase);
                      });
                  } else {
                      // If the service is currently translating something, wait up to two seconds for the translations to complete.
                      let count = 0;
                      const interval = setInterval(() => {
                          count++;
                          if (!this.isTranslating) {
                              // If the translations are complete, find the translation for the original phrase.
                              clearInterval(interval);
                              const newTranslation = this.allTranslations.find((t) => t.original_text === phrase)?.translated_text ?? phrase;
                              // Resolve the promise with the new translation, or with the original phrase if no translation was found.
                              resolve(newTranslation);
                          } else if (count > 50) {
                              // If the translations are not complete after two seconds, resolve the promise with the original phrase.
                              clearInterval(interval);
                              resolve(phrase);
                          }
                      }, 100);
                  }
              }
          } else {
              // If the translation service is not activated or ready, resolve the promise with the original phrase.
              resolve(phrase);
          }
      });
  }

  // requirePointCache=false lets pages without a point cache (e.g. registration) initialize translations
  async instantiateTranslations(isActivated: boolean, requirePointCache: boolean = true, autoSelectLanguage: boolean = false): Promise<void> {
    if (!isActivated || !topicData.isReady || (requirePointCache && !pointCache.isReady) || !isDefined(topicData.topic?.translation_languages) ||topicData.topic?.translation_languages.length === 0) {
      return;
    }
    // if the topics languages include french, use weglot key 2 otherwise use weglot key 1
    const languages = topicData.topic?.translation_languages;
    const language = (languages ?? []).find((l) => l.language === 'fr')?.language;
    if (!isDefined(language)) {
      (window as any).Weglot.initialize({
          api_key: HEADERS['X-Weglot-Key-Two'],
          originalLanguage: 'en',
          hide_switcher: true,
          cache: true,
      });
    }
    else if (languages && languages.length > 0) {
      (window as any).Weglot.initialize({
          api_key: HEADERS['X-Weglot-Key-One'],
          originalLanguage: 'en',
          hide_switcher: true,
          cache: true,
      });
    }


    this.isActivated = isActivated;
    document.documentElement.lang = this.language;

    // get all categories and tags from the topicdata and add them to the phrases
    const categories = topicData.categories.map((category) => category.name);
    const tags = topicData.allUserTags.map((tag) => tag.name);
    const topTags = topicData.topLevelUserTags.map((tag) => tag.name);
    this.baseMapTerms = categories.concat(tags);
    this.baseMapTerms = this.baseMapTerms.concat(topTags);
    this.baseMapTerms = this.baseMapTerms.concat(categories);

    this.coreTranslations = await this.translatePhrases(defaultPhrases.concat(this.baseMapTerms), this.language);
    this.allTranslations = this.coreTranslations.concat(this.newTranslations);
    // Overrides belong to the topic's org, not the viewer's — anonymous participants have no organizationInfo.
    const overridesOrganizationId = topicData.topic?.organization_id?.$oid ?? organizationInfo?.id?.$oid;
    if (isValidString(overridesOrganizationId)) {
      this.allTranslationSets = await getAllTranslations(TranslationData.amplitude_callsite, overridesOrganizationId);
      // find the TranslationData object with the current language and set the translationset to the translations
      this.translationSet = this.allTranslationSets.find((t) => t.language === this.language)?.translations ?? [];
    }

    // add listener for new translations
    this.Weglot.on('languageChanged', async (e: any) => {
      await this.changeLanguage(this.language, e);
    }
    );

    // translate the core phrases and store the responses in the coreTranslations array
    this.isReady = true;

    if (autoSelectLanguage) {
      const initialLanguage = this.resolveInitialLanguage(languages ?? []);
      if (initialLanguage !== this.language) {
        await this.changeLanguage(this.language, initialLanguage);
      }
    }
    // Set after the initial choice so only picks made in the switcher are remembered.
    this.autoSelectLanguage = autoSelectLanguage;
  }

  private get rememberedLanguageKey(): string | undefined {
    const topicId = topicData.topicId;
    return isValidString(topicId) ? `proxi_language_${topicId}` : undefined;
  }

  // Remembered pick → experience default → device language → English. Only offered languages count.
  // An unset default means "automatic"; 'en' is an explicit English default that skips device detection.
  private resolveInitialLanguage(languages: TranslationLanguage[]): string {
    const enabled = new Set(languages.map((l) => l.language.toLowerCase()));
    enabled.add('en');

    const remembered = this.rememberedLanguageKey ? getFromLocalStorage(this.rememberedLanguageKey) : null;
    if (isValidString(remembered) && enabled.has(remembered)) {
      return remembered;
    }

    const preset = topicData.topic?.default_language?.toLowerCase();
    if (isValidString(preset) && enabled.has(preset)) {
      return preset;
    }

    const deviceLanguages = navigator.languages?.length ? navigator.languages : [navigator.language ?? ''];
    const device = deviceLanguages
      .map((l) => {
        const normalized = l.toLowerCase();
        // This app uses 'tw' for Traditional Chinese.
        return /^zh-(tw|hk|mo|hant)(?:-|$)/.test(normalized) ? 'tw' : normalized.split('-')[0];
      })
      .find((l) => enabled.has(l));
    return device ?? 'en';
  }

  getAppropriateAPI(language: string): string {
    if (language === 'fr') {
      return HEADERS['X-Weglot-Key-Two'];
    }
    return HEADERS['X-Weglot-Key-One'];
  }

  async translatePhrases(phrases: string[], language: string): Promise<TranslationElement[]> {
      if (language === 'en') {
        return phrases.map((phrase) => {
          return { original_text: phrase, translated_text: phrase, language: 'en', id: '' };
        });
      }

      const words = phrases.map((phrase, index) => {
        return { w: phrase || '', t: 1 };
      });

      // create the body for the API call
      const body = {
          l_from: 'en',
          l_to: language,
          request_url: 'https://betamap.proxi.co',
          words
      };
      const apiKey = this.getAppropriateAPI(language);

      // call the API
      const response = await fetch(`https://api.weglot.com/translate?api_key=${apiKey}`, {
          method: 'POST',
          headers: {
              'Content-Type': 'application/json'
          },
          body: JSON.stringify(body)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: TranslationApiResponse = await response.json();

      // map the response to an array of TranslationElement objects
      const translations: TranslationElement[] = data.from_words.map((word: string, index: number) => {
        return {
          original_text: word,
          translated_text: data.to_words[index],
          language: data.l_to,
          id: data.ids[index] !== undefined ? data.ids[index].toString() : ''
        };
      });

      return translations;
  }

  async changeLanguage(original_language: string, new_language: string): Promise<void> {

    if (original_language === new_language) {
      return;
    }
    this.storedTranslations[original_language] = this.allTranslations;
    this.translationSet = this.allTranslationSets.find((t) => t.language === new_language)?.translations ?? [];

    // if storedTranslations has the language, set allTranslations to the storedTranslations
    if (Object.keys(isDefined(this.storedTranslations[new_language]) || {}).length > 0) {
      this.allTranslations = this.storedTranslations[new_language];
      this.newTranslations = [];
    } else {
        // if storedTranslations does not have the language, translate the core phrases and store the responses in the coreTranslations array
        // create string array of phrases to translate of core phrases and new phrases
        const phrasesToTranslate = this.coreTranslations.concat(this.newTranslations).map((t) => t.original_text);
        this.coreTranslations = await this.translatePhrases(phrasesToTranslate, new_language);
        this.allTranslations = [];
        this.newTranslations = [];
        this.allTranslations = this.coreTranslations.concat(this.newTranslations);
    }
    this.language = new_language;
    document.documentElement.lang = new_language;
    if (this.autoSelectLanguage && this.rememberedLanguageKey) {
      setLocalStorage(this.rememberedLanguageKey, new_language);
    }
    this.eventTarget.dispatchEvent(new Event('languageChanged'));
  }

  originalTextChanged() {
    this.eventTarget.dispatchEvent(new Event('originalTextChanged'));
  }

  async translatePendingPhrases(): Promise<void> {
    if (this.toBeTranslated.length > 0) {
      await new Promise(resolve => setTimeout(resolve, 1000));
      if (this.toBeTranslated.length > 0) {
        const phrasesToTranslate = [...this.toBeTranslated];
        this.toBeTranslated = [];
        const translations = await this.translatePhrases(phrasesToTranslate, this.language);
        this.newTranslations.push(...translations);
        this.allTranslations.push(...translations);
      }
    }
    this.isTranslating = false;
  }

  async addCustomTranslation(translation:TranslationElement, amplitude_callsite: string): Promise<void> {

    if (organizationInfo.id?.$oid === '' || !isDefined(organizationInfo.id)) {
      return;
    }
    await addTranslation(TranslationData.amplitude_callsite, organizationInfo.id.$oid, translation);
    const language = translation.language;
    this.allTranslationSets = this.allTranslationSets.map((t) => {
      if (t.language === language) {
        t.translations.unshift(translation);
      }
      return t;
    }
    );
    if (language === this.language) {
      this.translationSet.unshift(translation);
      this.allTranslations.unshift(translation);
    } else {
      this.storedTranslations[language] = this.storedTranslations[language] ?? [];
      this.storedTranslations[language].unshift(translation);
    }
  }
}

const translationData = new TranslationData();

const defaultPhrases: string[] = [
  'Add Point',
  'Add Topic',
  'Show Map Details',
  'Hide Map Details',
  'Updated',
  'About This Map',
  'Get Directions',
  'Advanced Filters',
  'Near Me',
  'Map Details',
  'Advanced Filters',
  'Name',
  'Email',
  'Additional Info',
  'Submit',
  'Phone',
  'Checking in... This may take a few seconds.',
];


export default translationData;

